/**
 * API client for Prune Admin Tool Backend
 */

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8002";

export interface ApiError {
  detail: string;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: string,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem("auth_token");
}

/**
 * Set auth token in localStorage
 */
export function setAuthToken(token: string): void {
  localStorage.setItem("auth_token", token);
}

/**
 * Remove auth token from localStorage
 */
export function removeAuthToken(): void {
  localStorage.removeItem("auth_token");
}

/**
 * Handle session expiration by clearing auth and redirecting to login
 * This is called when a 401 response is detected
 */
function handleSessionExpiration(): void {
  // Clear the auth token
  removeAuthToken();
  
  // Only redirect if we're not already on the login page
  // This prevents redirect loops
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

/**
 * Make an authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Handle 401 Unauthorized - session expired
    if (response.status === 401) {
      handleSessionExpiration();
      // Throw error after handling redirect so callers can handle it if needed
      throw new ApiClientError(
        "Session expired. Please log in again.",
        response.status,
        "Session expired",
      );
    }

    let detail: string | undefined;
    try {
      const errorData = await response.json();
      // FastAPI validation errors return detail as an array of objects
      if (Array.isArray(errorData.detail)) {
        detail = errorData.detail
          .map((err: { msg: string; loc: string[] }) => {
            const field = err.loc.slice(1).join(".");
            return `${field}: ${err.msg}`;
          })
          .join(", ");
      } else {
        detail = errorData.detail || errorData.message || response.statusText;
      }
    } catch {
      detail = response.statusText;
    }
    throw new ApiClientError(
      `API request failed: ${response.statusText}`,
      response.status,
      detail,
    );
  }

  // Handle empty responses
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return response.text() as unknown as T;
}

/**
 * API client methods
 */
export const api = {
  /**
   * Authentication endpoints
   */
  auth: {
    async register(
      email: string,
      password: string,
      displayName?: string,
    ): Promise<User> {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
          display_name: displayName || "User",
        }),
      });

      if (!response.ok) {
        let detail: string;
        try {
          const errorData = await response.json();
          detail = errorData.detail || "Registration failed";
        } catch {
          detail = response.statusText;
        }
        throw new ApiClientError(detail, response.status, detail);
      }

      return response.json();
    },

    async login(
      email: string,
      password: string,
    ): Promise<{ access_token: string }> {
      const formData = new FormData();
      formData.append("username", email);
      formData.append("password", password);

      const response = await fetch(`${API_BASE_URL}/auth/bearer/login`, {
        method: "POST",
        // Don't set Content-Type header - browser will set it with boundary for FormData
        headers: {},
        body: formData,
      });

      if (!response.ok) {
        let detail: string;
        try {
          const errorData = await response.json();
          detail = errorData.detail || "Login failed";
        } catch {
          detail = response.statusText;
        }
        throw new ApiClientError(detail, response.status, detail);
      }

      const data = await response.json();
      return data;
    },

    async getCurrentUser(): Promise<User> {
      return apiRequest<User>("/users/me");
    },

    async logout(): Promise<void> {
      try {
        await apiRequest<void>("/auth/bearer/logout", {
          method: "POST",
        });
      } catch (error) {
        // Ignore errors - we'll still clear local state
        console.error("Logout error:", error);
      }
    },
  },

  /**
   * URL endpoints
   */
  urls: {
    async list(
      limit?: number,
      fromId?: string,
      includePagination?: boolean,
    ): Promise<
      URL[] | { items: URL[]; total: number; has_next_page: boolean }
    > {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (fromId) params.append("from_id", fromId);
      if (includePagination) params.append("include_pagination", "true");
      const query = params.toString();
      return apiRequest<
        URL[] | { items: URL[]; total: number; has_next_page: boolean }
      >(`/api/urls${query ? `?${query}` : ""}`);
    },

    async get(urlId: string): Promise<URL> {
      return apiRequest<URL>(`/api/urls/${urlId}`);
    },

    async updateStatus(urlId: string, status: string): Promise<URL> {
      return apiRequest<URL>(`/api/urls/${urlId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
    },

    async getRescans(urlId: string, limit?: number): Promise<URLRescan[]> {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      const query = params.toString();
      return apiRequest<URLRescan[]>(
        `/api/urls/${urlId}/rescans${query ? `?${query}` : ""}`,
      );
    },

    async getStatusChanges(
      urlId: string,
      limit?: number,
    ): Promise<URLStatusChange[]> {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      const query = params.toString();
      return apiRequest<URLStatusChange[]>(
        `/api/urls/${urlId}/status-changes${query ? `?${query}` : ""}`,
      );
    },

    async getAgencies(urlId: string): Promise<URLAgency[]> {
      return apiRequest<URLAgency[]>(`/api/urls/${urlId}/agencies`);
    },

    async getReviewQueue(limit?: number): Promise<URLReview[]> {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      const query = params.toString();
      return apiRequest<URLReview[]>(
        `/api/urls/review-queue${query ? `?${query}` : ""}`,
      );
    },

    async submitReview(
      urlId: string,
      classification: string,
      secondaryFlags: Record<string, boolean>,
      notes: string | null,
      action: "confirm_down" | "mark_live",
    ): Promise<URL> {
      return apiRequest<URL>(`/api/urls/${urlId}/review`, {
        method: "POST",
        body: JSON.stringify({
          classification,
          secondary_flags: secondaryFlags,
          notes,
          action,
        }),
      });
    },
  },

  /**
   * User endpoints
   */
  users: {
    async list(
      limit?: number,
      isActive?: boolean,
      isSuperuser?: boolean,
    ): Promise<User[]> {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (isActive !== undefined)
        params.append("is_active", isActive.toString());
      if (isSuperuser !== undefined)
        params.append("is_superuser", isSuperuser.toString());
      const query = params.toString();
      return apiRequest<User[]>(`/api/users${query ? `?${query}` : ""}`);
    },

    async get(userId: string): Promise<User> {
      return apiRequest<User>(`/api/users/${userId}`);
    },

    async update(userId: string, updates: Partial<User>): Promise<User> {
      return apiRequest<User>(`/api/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
    },
  },

  /**
   * Case endpoints
   */
  cases: {
    async list(
      limit?: number,
      caseIds?: string[],
      includePagination?: boolean,
    ): Promise<
      Case[] | { items: Case[]; total: number; has_next_page: boolean }
    > {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (includePagination) params.append("include_pagination", "true");
      if (caseIds && caseIds.length > 0) {
        caseIds.forEach((id) => params.append("case_ids", id));
      }
      const query = params.toString();
      return apiRequest<
        Case[] | { items: Case[]; total: number; has_next_page: boolean }
      >(`/api/cases${query ? `?${query}` : ""}`);
    },

    async get(caseId: string): Promise<Case> {
      return apiRequest<Case>(`/api/cases/${caseId}`);
    },

    async create(name: string, type: string): Promise<Case> {
      return apiRequest<Case>("/api/cases", {
        method: "POST",
        body: JSON.stringify({ name, type }),
      });
    },

    async getUrls(caseId: string): Promise<URL[]> {
      return apiRequest<URL[]>(`/api/cases/${caseId}/urls`);
    },

    async addUrls(caseId: string, urls: string[]): Promise<URL[]> {
      return apiRequest<URL[]>(`/api/cases/${caseId}/urls`, {
        method: "POST",
        body: JSON.stringify({ urls }),
      });
    },

    async getStatistics(caseId: string): Promise<CaseStatistics> {
      return apiRequest<CaseStatistics>(`/api/cases/${caseId}/statistics`);
    },

    async getTextMetadata(
      caseId: string,
      source?: string,
    ): Promise<CaseMetadataText[]> {
      const params = new URLSearchParams();
      if (source) params.append("source", source);
      const query = params.toString();
      return apiRequest<CaseMetadataText[]>(
        `/api/cases/${caseId}/metadata/text${query ? `?${query}` : ""}`,
      );
    },

    async getImageMetadata(
      caseId: string,
      source?: string,
    ): Promise<CaseMetadataImage[]> {
      const params = new URLSearchParams();
      if (source) params.append("source", source);
      const query = params.toString();
      return apiRequest<CaseMetadataImage[]>(
        `/api/cases/${caseId}/metadata/images${query ? `?${query}` : ""}`,
      );
    },

    async addTextMetadata(
      caseId: string,
      contents: string,
      source: string,
    ): Promise<CaseMetadataText> {
      return apiRequest<CaseMetadataText>(
        `/api/cases/${caseId}/metadata/text`,
        {
          method: "POST",
          body: JSON.stringify({ contents, source }),
        },
      );
    },

    async deleteTextMetadata(
      caseId: string,
      metadataId: number,
    ): Promise<{ message: string }> {
      return apiRequest<{ message: string }>(
        `/api/cases/${caseId}/metadata/text/${metadataId}`,
        {
          method: "DELETE",
        },
      );
    },
  },

  /**
   * Email template endpoints
   */
  emailTemplates: {
    async list(limit?: number): Promise<EmailTemplate[]> {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      const query = params.toString();
      return apiRequest<EmailTemplate[]>(
        `/api/email-templates${query ? `?${query}` : ""}`,
      );
    },

    async get(templateId: string): Promise<EmailTemplate> {
      return apiRequest<EmailTemplate>(`/api/email-templates/${templateId}`);
    },

    async create(data: CreateEmailTemplateRequest): Promise<EmailTemplate> {
      return apiRequest<EmailTemplate>("/api/email-templates", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    async update(
      templateId: string,
      data: UpdateEmailTemplateRequest,
    ): Promise<EmailTemplate> {
      return apiRequest<EmailTemplate>(`/api/email-templates/${templateId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },

    async delete(templateId: string): Promise<void> {
      return apiRequest<void>(`/api/email-templates/${templateId}`, {
        method: "DELETE",
      });
    },
  },

  /**
   * Agency endpoints
   */
  agencies: {
    async list(limit?: number): Promise<Agency[]> {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      const query = params.toString();
      return apiRequest<Agency[]>(`/api/agencies${query ? `?${query}` : ""}`);
    },

    async get(agencyId: string): Promise<Agency> {
      return apiRequest<Agency>(`/api/agencies/${agencyId}`);
    },

    async create(
      name: string,
      abbreviation?: string,
      contactEmail?: string,
    ): Promise<Agency> {
      return apiRequest<Agency>("/api/agencies", {
        method: "POST",
        body: JSON.stringify({
          name,
          abbreviation,
          contact_email: contactEmail,
        }),
      });
    },

    async update(agencyId: string, updates: Partial<Agency>): Promise<Agency> {
      return apiRequest<Agency>(`/api/agencies/${agencyId}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
    },

    async delete(agencyId: string): Promise<void> {
      return apiRequest<void>(`/api/agencies/${agencyId}`, {
        method: "DELETE",
      });
    },
  },

  /**
   * Domain endpoints
   */
  domains: {
    async list(
      limit?: number,
      offset?: number,
      includePagination?: boolean,
    ): Promise<
      Domain[] | { items: Domain[]; total: number; has_next_page: boolean }
    > {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (offset) params.append("offset", offset.toString());
      if (includePagination) params.append("include_pagination", "true");
      const query = params.toString();
      return apiRequest<
        Domain[] | { items: Domain[]; total: number; has_next_page: boolean }
      >(`/api/domains${query ? `?${query}` : ""}`);
    },

    async get(domainId: string): Promise<Domain> {
      return apiRequest<Domain>(`/api/domains/${domainId}`);
    },

    async create(data: CreateDomainRequest): Promise<Domain> {
      return apiRequest<Domain>("/api/domains", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    async update(domainId: string, data: UpdateDomainRequest): Promise<Domain> {
      return apiRequest<Domain>(`/api/domains/${domainId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },

    async delete(domainId: string): Promise<void> {
      return apiRequest<void>(`/api/domains/${domainId}`, {
        method: "DELETE",
      });
    },

    async sync(): Promise<{ synced_count: number }> {
      return apiRequest<{ synced_count: number }>("/api/domains/sync", {
        method: "POST",
      });
    },
  },

  /**
   * Hosting Provider endpoints
   */
  hostingProviders: {
    async list(
      limit?: number,
      offset?: number,
      includePagination?: boolean,
    ): Promise<
      | HostingProvider[]
      | { items: HostingProvider[]; total: number; has_next_page: boolean }
    > {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (offset) params.append("offset", offset.toString());
      if (includePagination) params.append("include_pagination", "true");
      const query = params.toString();
      return apiRequest<
        | HostingProvider[]
        | { items: HostingProvider[]; total: number; has_next_page: boolean }
      >(`/api/hosting-providers${query ? `?${query}` : ""}`);
    },

    async get(providerId: string): Promise<HostingProvider> {
      return apiRequest<HostingProvider>(
        `/api/hosting-providers/${providerId}`,
      );
    },

    async create(data: CreateHostingProviderRequest): Promise<HostingProvider> {
      return apiRequest<HostingProvider>("/api/hosting-providers", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    async update(
      providerId: string,
      data: UpdateHostingProviderRequest,
    ): Promise<HostingProvider> {
      return apiRequest<HostingProvider>(
        `/api/hosting-providers/${providerId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
      );
    },

    async delete(providerId: string): Promise<void> {
      return apiRequest<void>(`/api/hosting-providers/${providerId}`, {
        method: "DELETE",
      });
    },
  },

  /**
   * Registrar endpoints
   */
  registrars: {
    async list(
      limit?: number,
      offset?: number,
      includePagination?: boolean,
    ): Promise<
      | Registrar[]
      | { items: Registrar[]; total: number; has_next_page: boolean }
    > {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (offset) params.append("offset", offset.toString());
      if (includePagination) params.append("include_pagination", "true");
      const query = params.toString();
      return apiRequest<
        | Registrar[]
        | { items: Registrar[]; total: number; has_next_page: boolean }
      >(`/api/registrars${query ? `?${query}` : ""}`);
    },

    async get(registrarId: string): Promise<Registrar> {
      return apiRequest<Registrar>(`/api/registrars/${registrarId}`);
    },

    async create(data: CreateRegistrarRequest): Promise<Registrar> {
      return apiRequest<Registrar>("/api/registrars", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    async update(
      registrarId: string,
      data: UpdateRegistrarRequest,
    ): Promise<Registrar> {
      return apiRequest<Registrar>(`/api/registrars/${registrarId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },

    async delete(registrarId: string): Promise<void> {
      return apiRequest<void>(`/api/registrars/${registrarId}`, {
        method: "DELETE",
      });
    },
  },

  /**
   * Infrastructure endpoints
   */
  infrastructure: {
    async list(
      limit?: number,
      infrastructureType?: string,
    ): Promise<InfrastructureRegistry[]> {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (infrastructureType)
        params.append("infrastructure_type", infrastructureType);
      const query = params.toString();
      return apiRequest<InfrastructureRegistry[]>(
        `/api/infrastructure${query ? `?${query}` : ""}`,
      );
    },

    async get(infrastructureId: string): Promise<InfrastructureRegistry> {
      return apiRequest<InfrastructureRegistry>(
        `/api/infrastructure/${infrastructureId}`,
      );
    },

    async create(
      data: CreateInfrastructureRequest,
    ): Promise<InfrastructureRegistry> {
      return apiRequest<InfrastructureRegistry>("/api/infrastructure", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    async update(
      infrastructureId: string,
      data: UpdateInfrastructureRequest,
    ): Promise<InfrastructureRegistry> {
      return apiRequest<InfrastructureRegistry>(
        `/api/infrastructure/${infrastructureId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
      );
    },

    async delete(infrastructureId: string): Promise<void> {
      return apiRequest<void>(`/api/infrastructure/${infrastructureId}`, {
        method: "DELETE",
      });
    },
  },

  /**
   * Payment Processor endpoints (read-only, extracted from HAR)
   */
  paymentProcessors: {
    async list(
      limit?: number,
      offset?: number,
      includePagination?: boolean,
    ): Promise<
      | PaymentProcessor[]
      | { items: PaymentProcessor[]; total: number; has_next_page: boolean }
    > {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (offset) params.append("offset", offset.toString());
      if (includePagination) params.append("include_pagination", "true");
      const query = params.toString();
      return apiRequest<
        | PaymentProcessor[]
        | { items: PaymentProcessor[]; total: number; has_next_page: boolean }
      >(`/api/payment-processors${query ? `?${query}` : ""}`);
    },
  },

  /**
   * Ad Tech endpoints (read-only, extracted from HAR)
   */
  adTech: {
    async list(
      limit?: number,
      offset?: number,
      includePagination?: boolean,
    ): Promise<
      AdTech[] | { items: AdTech[]; total: number; has_next_page: boolean }
    > {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (offset) params.append("offset", offset.toString());
      if (includePagination) params.append("include_pagination", "true");
      const query = params.toString();
      return apiRequest<
        AdTech[] | { items: AdTech[]; total: number; has_next_page: boolean }
      >(`/api/ad-tech${query ? `?${query}` : ""}`);
    },
  },

  /**
   * CDN endpoints (read-only, extracted from evidence packages)
   */
  cdns: {
    async list(
      limit?: number,
      offset?: number,
      includePagination?: boolean,
    ): Promise<
      CDN[] | { items: CDN[]; total: number; has_next_page: boolean }
    > {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (offset) params.append("offset", offset.toString());
      if (includePagination) params.append("include_pagination", "true");
      const query = params.toString();
      return apiRequest<
        CDN[] | { items: CDN[]; total: number; has_next_page: boolean }
      >(`/api/cdns${query ? `?${query}` : ""}`);
    },
  },

  /**
   * Evidence package endpoints
   */
  evidencePackages: {
    async list(params?: {
      caseName?: string;
      limit?: number;
      offset?: number;
      originalUrl?: string;
      siteStatus?: string;
      enrichmentStatus?: string;
    }): Promise<EvidencePackage[]> {
      const searchParams = new URLSearchParams();
      if (params?.caseName) searchParams.append("case_name", params.caseName);
      if (params?.limit) searchParams.append("limit", params.limit.toString());
      if (params?.offset)
        searchParams.append("offset", params.offset.toString());
      if (params?.originalUrl)
        searchParams.append("original_url", params.originalUrl);
      if (params?.siteStatus)
        searchParams.append("site_status", params.siteStatus);
      if (params?.enrichmentStatus)
        searchParams.append("enrichment_status", params.enrichmentStatus);
      const query = searchParams.toString();
      return apiRequest<EvidencePackage[]>(
        `/api/evidence-packages${query ? `?${query}` : ""}`,
      );
    },

    async get(packageId: string): Promise<EvidencePackage> {
      return apiRequest<EvidencePackage>(`/api/evidence-packages/${packageId}`);
    },
  },

  /**
   * Escalation email endpoints
   */
  escalationEmails: {
    async listForUrl(
      urlId: string,
      limit?: number,
    ): Promise<EscalationEmail[]> {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      const query = params.toString();
      return apiRequest<EscalationEmail[]>(
        `/api/escalation-emails/url/${urlId}${query ? `?${query}` : ""}`,
      );
    },

    async get(emailId: string): Promise<EscalationEmail> {
      return apiRequest<EscalationEmail>(`/api/escalation-emails/${emailId}`);
    },
  },

  /**
   * Review Notices endpoints
   */
  reviewNotices: {
    async list(
      status?: string,
      escalationType?: string,
    ): Promise<ReviewNotice[]> {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (escalationType) params.append("escalation_type", escalationType);
      const query = params.toString();
      return apiRequest<ReviewNotice[]>(
        `/api/review-notices${query ? `?${query}` : ""}`,
      );
    },

    async get(noticeId: number): Promise<ReviewNotice> {
      return apiRequest<ReviewNotice>(`/api/review-notices/${noticeId}`);
    },

    async approve(escalationStepId: number): Promise<ReviewNotice> {
      return apiRequest<ReviewNotice>(
        `/api/review-notices/${escalationStepId}/approve`,
        {
          method: "POST",
        },
      );
    },

    async update(
      escalationStepId: number,
      data: {
        to_email?: string;
        subject?: string;
        body?: string;
      },
    ): Promise<ReviewNotice> {
      return apiRequest<ReviewNotice>(
        `/api/review-notices/${escalationStepId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
      );
    },
  },
};

/**
 * Type definitions matching backend models
 */
export interface User {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  display_name: string | null;
  agency_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Case {
  id: string;
  name: string;
  type: string;
  status: string;
  sunset_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Agency {
  id: string;
  name: string;
  abbreviation: string | null;
  contact_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplate {
  id: string;
  template_name: string;
  subject_line: string;
  type: string;
  recipient_type: string;
  body?: string;
  usage_count: number;
  last_modified: string;
  modified_by: string;
}

export interface URL {
  id: string;
  url: string;
  view_count: number;
  status: string | null;
  last_checked: string | null;
  created_at?: string;
  updated_at?: string;
  // Enriched fields
  case_name?: string | null;
  case_created_at?: string | null;
  user_display_name?: string | null;
  agency_name?: string | null;
  days_live?: number | null;
}

export interface URLRescan {
  id: string;
  url_id: string;
  rescan_timestamp: string;
  status: string;
  view_count: number | null;
  page_type: string | null;
  media_type: string | null;
  upload_date: string | null;
  discovery_source: string | null;
  status_changed: boolean;
  previous_status: string | null;
  change_type: string | null;
  created_at: string;
}

export interface URLStatusChange {
  id: string;
  url_id: string;
  rescan_id: string;
  previous_status: string;
  new_status: string;
  change_type: string;
  change_timestamp: string;
  alert_sent: boolean;
  alert_sent_at: string | null;
  created_at: string;
}

export interface URLReview {
  id: string;
  url: string;
  case: string;
  caseId: string;
  agency: string;
  detectedIssue: string;
  httpStatus: string;
  rawError: string;
  detectedDate: string;
  lastLiveDate: string | null;
  capturedHtml: string | null;
}

export interface Domain {
  id: string;
  domain: string;
  ncii_instances: number;
  hosting_provider: string;
  registrar: string;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number;
  avg_response_time: number;
  technologies: string[];
}

export interface CreateDomainRequest {
  domain: string;
  registrar?: string | null;
  hosting_provider?: string | null;
}

export interface UpdateDomainRequest {
  registrar?: string | null;
  hosting_provider?: string | null;
}

export interface HostingProvider {
  id: string;
  name: string;
  domain_count: number;
  total_records: number;
}

export interface CreateHostingProviderRequest {
  name: string;
}

export interface UpdateHostingProviderRequest {
  name?: string | null;
}

export interface Registrar {
  id: string;
  name: string;
  domain_count: number;
  total_whois_records: number;
}

export interface CreateRegistrarRequest {
  name: string;
}

export interface UpdateRegistrarRequest {
  name?: string | null;
}

export interface InfrastructureRegistry {
  id: number;
  name: string;
  type: string;
  category?: string | null;
  description?: string | null;
  domain_patterns: string[];
  script_patterns: string[];
  detection_methods: string[];
  data_types_available: string[];
  contact_methods?: Record<string, any> | null;
  legal_process_info?: Record<string, any> | null;
  detection_confidence?: string | null;
  total_detections?: number | null;
  unique_cases_detected?: number | null;
  first_detected_at?: string | null;
  last_updated_at?: string | null;
  created_by?: string | null;
}

export interface CreateInfrastructureRequest {
  name: string;
  type: string;
  category?: string | null;
  description?: string | null;
  domain_patterns?: string[];
  script_patterns?: string[];
  detection_methods?: string[];
  data_types_available?: string[];
  contact_methods?: Record<string, any> | null;
  legal_process_info?: Record<string, any> | null;
  detection_confidence?: string | null;
}

export interface UpdateInfrastructureRequest {
  name?: string | null;
  type?: string | null;
  category?: string | null;
  description?: string | null;
  domain_patterns?: string[] | null;
  script_patterns?: string[] | null;
  detection_methods?: string[] | null;
  data_types_available?: string[] | null;
  contact_methods?: Record<string, any> | null;
  legal_process_info?: Record<string, any> | null;
  detection_confidence?: string | null;
}

export interface CreateEmailTemplateRequest {
  template_name: string;
  description?: string | null;
  case_type: string;
  recipient_type: string;
  template_type: string;
  requires_case_number?: boolean;
  subject_line: string;
  body: string;
}

export interface UpdateEmailTemplateRequest {
  template_name?: string | null;
  description?: string | null;
  case_type?: string | null;
  recipient_type?: string | null;
  template_type?: string | null;
  requires_case_number?: boolean | null;
  subject_line?: string | null;
  body?: string | null;
}

export interface PaymentProcessor {
  processor_name: string;
  ncii_instances: number;
  type?: string | null;
  region?: string | null;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number;
  avg_response_time?: number | null;
  contacts: string[];
}

export interface AdTech {
  company_name: string;
  ncii_instances: number;
  type?: string | null;
  region?: string | null;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number;
  avg_response_time?: number | null;
  contacts: string[];
}

export interface CDN {
  cdn_name: string;
  ncii_instances: number;
  type?: string | null;
  region?: string | null;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number;
  avg_response_time?: number | null;
  contacts: string[];
}

export interface EvidencePackage {
  id: string;
  sunset_id: string | null;
  user_id: string | null;
  original_url: string;
  case_name: string;
  site_status: string | null;
  page_type: string | null;
  media_type: string | null;
  upload_date: string | null;
  discovery_source: string | null;
  enrichment_status: string | null;
  screenshot_s3_path: string | null;
  har_s3_path: string | null;
  raw_dom_content: string | null;
  created_at: string;
  updated_at: string;
}

export interface EscalationEmail {
  id: number;
  case_id: string;
  urls_id: number;
  to_emails: string[];
  subject: string;
  body: string;
  step_number: number;
  recipient_type: string;
  status: string;
  scheduled_at: string | null;
  sent_at: string | null;
  message_id: string | null;
  sent_by: string;
  created_at: string;
  updated_at: string;
}

export interface URLAgency {
  id: string;
  name: string;
  abbreviation: string | null;
}

export interface ReviewNotice {
  id: number;
  escalation_id: number;
  escalation_step_id: number;
  subject_line: string;
  status: string;
  send_time: string | null;
  user_name: string;
  case_id: string;
  case_number: string | null;
  agency_name: string;
  recipient_email: string;
  escalation_type: string;
  message_body: string;
  agent_name: string;
  created_at: string;
}

export interface CaseStatistics {
  case_info: {
    id: string;
    name: string;
    type: string;
    status: string;
    case_number: string | null;
    created_at: string;
    updated_at: string;
  };
  agency: {
    id: string | null;
    name: string | null;
    abbreviation: string | null;
  };
  assigned_users: Array<{
    id: string;
    display_name: string | null;
    email: string;
    permission: string;
  }>;
  urls: {
    total_urls: number;
    status_breakdown: Record<string, number>;
    total_views: number;
  };
  evidence: {
    total_packages: number;
    unique_urls: number;
    site_status_breakdown: Record<string, number>;
    content_status_breakdown: Record<string, number>;
    first_capture: string | null;
    latest_capture: string | null;
  };
}

export interface CaseMetadataText {
  id: number;
  cases_id: string;
  source: string;
  uploaded_by: string | null;
  contents: string;
  created_at: string;
}

export interface CaseMetadataImage {
  id: number;
  cases_id: string;
  source: string;
  match_type: string;
  uploaded_by: string | null;
  contents: string; // Base64 encoded
  created_at: string;
}
